import MatrixClientPeg from 'matrix-react-sdk/lib/MatrixClientPeg'
import dis from 'matrix-react-sdk/lib/dispatcher'
import Timer from 'matrix-react-sdk/lib/utils/Timer'
import { debounce } from 'util/functions'
import isEqual from 'fast-deep-equal'
import { MatrixEvent } from 'matrix-js-sdk'

export const UNAVAILABLE_TIME_MS = 5 * 60 * 1000
export const OFFLINE_TIME_MS = 15 * 60 * 1000
export const KEEPALIVE_TIME_MS = 20 * 1000
export const PRESENCE_STATES = [
  'online',
  'offline',
  'unavailable',
  'org.matrix.msc3026.busy',
  'unknown',
]
const ACCOUNT_PRESENCE_EVENT_TYPE = 'g.presence'
const MAX_RATE_LIMIT_RETRIES = 3
const DEFAULT_RETRY_DELAY_MS = 1000
const MIN_RETRY_DELAY_MS = 100
const MAX_RETRY_DELAY_MS = 30000

export default class Presence {
  constructor() {
    this.autoPresenceTimer = null
    this.keepAliveTimer = null
    this.dispatcherRef = null
    this.client = null
    this.accountDataListener = null
    this.unavailableTimeMs = UNAVAILABLE_TIME_MS
    this.offlineTimeMs = OFFLINE_TIME_MS
    this.autoPresence = true
    this.state = 'unknown'
    this.confirmedState = 'offline'
    this.generation = 0
    this.processedGeneration = 0
    this.lifecycle = 0
    this.drainPromise = null
    this.activeEntry = null
    this.presenceQueue = []
    this.retryCancel = null
    this.persistence = Promise.resolve()
    this.onAction = this.onAction.bind(this)
  }

  initClient(client, onAccountPresence) {
    if (!client) return false
    this.disposeClient()
    const lifecycle = this.lifecycle
    this.client = client
    this.onAccountPresence = onAccountPresence
    this.generation = 0
    this.processedGeneration = 0
    this.drainingLifecycle = null
    this.persistence = Promise.resolve()
    const ownUser = client.getUser && client.getUser(client.getUserId())
    this.confirmedState =
      ownUser && PRESENCE_STATES.includes(ownUser.presence)
        ? ownUser.presence
        : 'offline'
    this.state = this.confirmedState
    this.accountDataListener = event => {
      if (lifecycle === this.lifecycle && client === this.client) {
        this.onAccountData(event)
      }
    }
    client.on('accountData', this.accountDataListener)
    this.onAccountData(
      client.getAccountData(ACCOUNT_PRESENCE_EVENT_TYPE) ||
        this.getDefaultPresenceEvent()
    )
    return true
  }

  onAccountData(event) {
    if (!event || event.getType() !== ACCOUNT_PRESENCE_EVENT_TYPE) return
    const content = event.getContent() || {}
    const { unavailableTimeMs, offlineTimeMs, state, autoPresence } = content
    if (
      Number.isFinite(unavailableTimeMs) &&
      this.unavailableTimeMs !== unavailableTimeMs
    ) {
      this.setAutoAway(unavailableTimeMs)
    }
    if (
      Number.isFinite(offlineTimeMs) &&
      this.offlineTimeMs !== offlineTimeMs
    ) {
      this.setAutoOffline(offlineTimeMs)
    }
    if (
      this.generation === this.processedGeneration &&
      state !== this.state &&
      PRESENCE_STATES.includes(state)
    ) {
      this.setState(state)
    }
    if (
      typeof autoPresence === 'boolean' &&
      this.autoPresence !== autoPresence
    ) {
      this.setAutoPresence(autoPresence)
    }
    if (this.onAccountPresence) {
      this.onAccountPresence(
        this.generation === this.processedGeneration
          ? event
          : new MatrixEvent({
              type: ACCOUNT_PRESENCE_EVENT_TYPE,
              content: { ...content, state: this.state },
            })
      )
    }
  }

  getDefaultPresenceEvent() {
    return new MatrixEvent({
      type: ACCOUNT_PRESENCE_EVENT_TYPE,
      content: this.constructSettingsContent(this.confirmedState),
    })
  }

  getPresenceEvent() {
    return this.client
      ? this.client.getAccountData(ACCOUNT_PRESENCE_EVENT_TYPE)
      : null
  }

  getSettings() {
    const event = this.getPresenceEvent()
    return event && event.getContent() ? event.getContent() : null
  }

  getAutoPresenceTimeoutMs(state) {
    if (state === 'online') return this.unavailableTimeMs
    if (['org.matrix.msc3026.busy', 'unavailable'].includes(state)) {
      return this.offlineTimeMs
    }
    return 24 * 60 * 60 * 1000
  }

  constructSettingsContent(state = this.confirmedState) {
    return {
      unavailableTimeMs: this.unavailableTimeMs,
      offlineTimeMs: this.offlineTimeMs,
      autoPresence: this.autoPresence,
      state,
    }
  }

  persistSettings(snapshot, client = this.client, lifecycle = this.lifecycle) {
    if (!client) return
    this.persistence = this.persistence.then(async () => {
      if (client !== this.client || lifecycle !== this.lifecycle) return
      const event = client.getAccountData(ACCOUNT_PRESENCE_EVENT_TYPE)
      const oldSettings = event && event.getContent()
      if (isEqual(oldSettings, snapshot)) return
      try {
        await client.setAccountData(ACCOUNT_PRESENCE_EVENT_TYPE, snapshot)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to save presence settings: %s', err)
      }
    })
  }

  saveSettings = debounce(() => {
    this.persistSettings(this.constructSettingsContent())
  }, 100)

  async start() {
    this.startKeepAlive()
    if (!this.autoPresence) return
    this.autoPresenceTimer = new Timer(
      this.getAutoPresenceTimeoutMs(this.getState())
    )
    this.dispatcherRef = dis.register(this.onAction)
    while (this.autoPresenceTimer) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await this.autoPresenceTimer.finished()
        if (this.getState() === 'online') {
          this.setState('unavailable')
          this.autoPresenceTimer = new Timer(
            this.getAutoPresenceTimeoutMs('unavailable')
          )
          this.autoPresenceTimer.start()
        } else {
          this.setState('offline')
        }
      } catch (e) {
        /* timer aborted */
      }
    }
  }

  async startKeepAlive() {
    if (this.keepAliveTimer) return
    this.keepAliveTimer = new Timer(KEEPALIVE_TIME_MS)
    this.keepAliveTimer.start()
    while (this.keepAliveTimer) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await this.keepAliveTimer.finished()
        if (['unavailable', 'org.matrix.msc3026.busy'].includes(this.state)) {
          this.setState(this.state, true)
        }
      } catch (e) {
        /* timer aborted */
      }
    }
  }

  stopTimers() {
    if (this.dispatcherRef) {
      dis.unregister(this.dispatcherRef)
      this.dispatcherRef = null
    }
    if (this.autoPresenceTimer) {
      this.autoPresenceTimer.abort()
      this.autoPresenceTimer = null
    }
    if (this.keepAliveTimer) {
      this.keepAliveTimer.abort()
      this.keepAliveTimer = null
    }
  }

  cancelRetry() {
    if (this.retryCancel) this.retryCancel()
  }

  disposeClient() {
    this.lifecycle += 1
    this.cancelRetry()
    this.saveSettings.cancel()
    if (this.client && this.accountDataListener && this.client.removeListener) {
      this.client.removeListener('accountData', this.accountDataListener)
    }
    this.client = null
    this.accountDataListener = null
    if (this.activeEntry) this.activeEntry.resolve()
    this.presenceQueue.forEach(entry => entry.resolve())
    this.presenceQueue = []
    this.drainPromise = null
    this.activeEntry = null
  }

  stop() {
    this.stopTimers()
    this.disposeClient()
  }

  updateTimers() {
    if (this.autoPresenceTimer) {
      this.autoPresenceTimer.restart()
      this.autoPresenceTimer.changeTimeout(
        this.getAutoPresenceTimeoutMs(this.state)
      )
    }
  }

  getState() {
    return this.state
  }

  onAction(payload) {
    if (
      this.state !== 'unknown' &&
      this.client &&
      payload.action === 'user_activity' &&
      this.autoPresence
    ) {
      if (this.state !== 'online') this.setState('online')
      else if (this.autoPresenceTimer) {
        this.autoPresenceTimer.restart()
        this.autoPresenceTimer.changeTimeout(
          this.getAutoPresenceTimeoutMs('online')
        )
      }
    }
  }

  async setAutoAway(unavailableTimeMs) {
    this.unavailableTimeMs = unavailableTimeMs
    this.updateTimers()
    this.saveSettings()
  }

  async setAutoOffline(offlineTimeMs) {
    this.offlineTimeMs = offlineTimeMs
    this.updateTimers()
    this.saveSettings()
  }

  async setAutoPresence(autoPresence) {
    if (autoPresence === null || this.autoPresence === autoPresence) return
    this.autoPresence = autoPresence
    if (!this.autoPresenceTimer && autoPresence === true) this.start()
    else if (this.autoPresenceTimer && autoPresence === false) this.stopTimers()
    this.saveSettings()
  }

  setState(newState, force = false) {
    if (!PRESENCE_STATES.includes(newState)) {
      return Promise.reject(new Error(`Bad presence state: ${newState}`))
    }
    if (force && this.activeEntry && newState === this.activeEntry.state) {
      return this.activeEntry.promise
    }
    const forcedSameState = force && newState === this.state
    if (this.keepAliveTimer) this.keepAliveTimer.restart()
    this.state = newState
    this.generation += 1
    MatrixClientPeg.updateSyncPresence(newState)
    let resolve
    const promise = new Promise(res => {
      resolve = res
    })
    this.presenceQueue.push({
      state: newState,
      generation: this.generation,
      forcedSameState,
      resolve,
      promise,
    })
    this.startDrain()
    return promise
  }

  startDrain() {
    if (this.drainPromise) return this.drainPromise
    const lifecycle = this.lifecycle
    const client = this.client
    const drainPromise = this.drainPresence(lifecycle, client)
    this.drainPromise = drainPromise
    const cleanup = error => {
      if (
        this.lifecycle !== lifecycle ||
        this.client !== client ||
        this.drainPromise !== drainPromise
      ) {
        return
      }
      if (error) {
        // eslint-disable-next-line no-console
        console.error('Presence coordinator failed: %s', error)
        if (this.activeEntry) this.activeEntry.resolve()
        this.activeEntry = null
        this.presenceQueue.forEach(entry => entry.resolve())
        this.presenceQueue = []
        this.processedGeneration = this.generation
        this.state = this.confirmedState
        MatrixClientPeg.updateSyncPresence(this.confirmedState)
        this.updateTimers()
      }
      this.drainPromise = null
      if (this.presenceQueue.length) this.startDrain()
    }
    drainPromise.then(() => cleanup(), cleanup)
    return drainPromise
  }

  sleep(ms) {
    return new Promise(resolve => {
      let timeout
      const finish = () => {
        clearTimeout(timeout)
        if (this.retryCancel === finish) this.retryCancel = null
        resolve()
      }
      timeout = setTimeout(finish, ms)
      this.retryCancel = finish
    })
  }

  isRateLimitError(err) {
    if (!err) return false
    if (typeof err.isRateLimitError === 'function' && err.isRateLimitError()) {
      return true
    }
    return err.httpStatus === 429 || err.errcode === 'M_LIMIT_EXCEEDED'
  }

  getRetryDelay(err) {
    const sdkDelay =
      typeof err.getRetryAfterMs === 'function'
        ? err.getRetryAfterMs()
        : err.data && err.data.retry_after_ms
    if (!Number.isFinite(sdkDelay)) return DEFAULT_RETRY_DELAY_MS
    return Math.min(MAX_RETRY_DELAY_MS, Math.max(MIN_RETRY_DELAY_MS, sdkDelay))
  }

  async drainPresence(lifecycle, client) {
    while (
      lifecycle === this.lifecycle &&
      client === this.client &&
      this.presenceQueue.length
    ) {
      const entry = this.presenceQueue.shift()
      this.activeEntry = entry
      const suppressPersistence =
        entry.forcedSameState && entry.state === this.confirmedState
      let retries = 0
      let failure = null
      while (lifecycle === this.lifecycle && client === this.client) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await client.setPresence(
            entry.state === 'unavailable'
              ? 'org.matrix.msc3026.busy'
              : entry.state
          )
          failure = null
          break
        } catch (err) {
          failure = err
          if (
            !this.isRateLimitError(err) ||
            retries >= MAX_RATE_LIMIT_RETRIES
          ) {
            break
          }
          if (lifecycle !== this.lifecycle || client !== this.client) return
          retries += 1
          // eslint-disable-next-line no-await-in-loop
          await this.sleep(this.getRetryDelay(err))
        }
      }
      if (lifecycle !== this.lifecycle || client !== this.client) return
      this.processedGeneration = entry.generation
      if (!failure) {
        if (!suppressPersistence) {
          this.persistSettings(
            this.constructSettingsContent(entry.state),
            client,
            lifecycle
          )
        }
        this.confirmedState = entry.state
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to set presence: %s', failure)
        if (entry.generation === this.generation) {
          this.state = this.confirmedState
          MatrixClientPeg.updateSyncPresence(this.confirmedState)
          this.updateTimers()
          if (!suppressPersistence && !this.isRateLimitError(failure)) {
            this.persistSettings(
              this.constructSettingsContent(this.confirmedState),
              client,
              lifecycle
            )
          }
        }
      }
      this.activeEntry = null
      entry.resolve()
    }
  }
}
